// Persisted browser-storage identities for the `evidence/library` family
// (ARCFE-1027 · C).
//
// `packages/CLAUDE.md` freezes query keys and PERSISTED keys under the same
// rule — both live in the `/contracts` key factory with the legacy site
// re-exporting. The string below is a durable `localStorage` key: changing it
// silently re-prompts every clinician who has already accepted the web-source
// risks, which is a behaviour change with no error to attribute it to.
//
// Only the identity moves. The read/write helpers stay in
// `src/components/library/libraryUploadRisk.ts` — they touch `window.
// localStorage`, and `/contracts` is inert.

/**
 * Web-source (URL) risk opt-in, deliberately distinct from the DB-backed
 * document-upload acceptance (`useLibraryUploadRiskAcceptance`, EVI-1698) so
 * accepting one does not silently waive the other — they cover different
 * risks (uploaded files vs externally-hosted web content).
 */
export const LIBRARY_SOURCE_URL_RISK_ACCEPTED_KEY = 'heidi.librarySource.urlRiskAccepted';

/**
 * Source Control toggle state, persisted since before the BE-backed sync
 * and still read as the warm cache on every mount. The string is a
 * live contract with every clinician's browser: renaming it resets their source
 * selection on their next load, with no error to attribute it to. Cleared from
 * `clearLogoutStorage` on full logout.
 *
 * Only the identity lives here. The atom that reads and writes it is
 * `src/atoms/sourceControlAtoms.ts`, because `/contracts` is inert and
 * `atomWithStorage` touches `window.localStorage`.
 */
export const SOURCE_CONTROL_TOGGLE_STORAGE_KEY = 'heidi:source-control-toggle';

/**
 * The backend default source selection (`evidence_settings.default_source_control`)
 * this browser last applied for a user who has saved none. A cached selection
 * equal to it is still a default rather than a choice, so it follows the
 * backend when that default changes or is withdrawn. Cleared from
 * `clearLogoutStorage` on full logout.
 *
 * Only the identity lives here. The read and write are
 * `src/atoms/sourceControlLocalStorage.ts`.
 */
export const SOURCE_CONTROL_APPLIED_SERVER_DEFAULT_STORAGE_KEY =
  'heidi:source-control-applied-server-default';

/**
 * The chat the user is currently looking at, persisted in `sessionStorage` so
 * it survives in-app navigation within a tab. Read as an authorisation check on
 * the two chat routes and on the embed — `chatId === getCurrentChatId()` is
 * what gates the panel — and written after a chat is created, so a changed
 * string does not merely lose state: it makes a freshly created chat look
 * unviewable to the tab that created it.
 *
 * Only the identity lives here. The `sessionStorage` read and write are
 * `src/utils/currentChatId.ts`, because `/contracts` is inert and those helpers
 * touch `globalThis.sessionStorage`.
 */
export const CURRENT_CHAT_ID_STORAGE_KEY = 'current_chat_id';

/**
 * The clinician's pending Evidence question, handed from the public `/chat`
 * page to the authenticated `/scribe/chat` page across the logged-in redirect.
 * It used to ride on the URL as `?query=`, which GA4 attached to every event
 * while GTM was still loaded from `/chat` (ARCFE-1182). `sessionStorage` keeps
 * it per tab, which is the scope of a redirect; the value is a JSON
 * `{ query, writtenAt }` and is treated as expired after sixty seconds so a
 * write that never reached its landing cannot replay days later.
 *
 * Only the identity lives here. The read, write and clear are
 * `packages/evidence/src/data/evidenceQueryHandoff.ts`, because `/contracts` is inert and those
 * helpers touch `globalThis.sessionStorage`.
 */
export const EVIDENCE_QUERY_HANDOFF_STORAGE_KEY = 'heidi:evidence-query-handoff';
